// =============================================================================
// App — ルーティング + AppShell マウント (2026-07-05 UI刷新 P0)
//
//   旧構成 (GlobalNavi + 自作 dropdown 2段 + standaloneLinks/groupNavs) を全廃し、
//   7エリア固定左レールの AppShell (components/AppShell.tsx) に置換。
//   - VibesProvider は既存ページ内の vibes 部品 (Button/Container 等) が使うため残す
//   - GlobalNavi / CustomNavDropdown / NAV_GROUPS は廃止 (React19 findDOMNode 問題の
//     温床だった react-transition-group 依存もシェルからは完全排除)
//   - readPageFromHash は全 hash 後方互換 (旧 deep link 維持)。
//     fallback は 'inbox' (受信箱=ホーム, council 6_ux_architect §1.1)
//   - CommandDock / JobTray / HealthBanner のマウント維持 (HealthBanner は
//     AppShell がトップバー直下に render)。InvoiceCoverageBanner はコンテンツ領域内へ
// =============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { VibesProvider } from '@freee_jp/vibes';
import '@freee_jp/vibes/css';
import { AppShell, PageKey } from './components/AppShell';
import { CommandDock } from './components/CommandDock';
import { JobTray } from './components/JobTray';
import { InvoiceCoverageBanner } from './components/InvoiceCoverageBanner';
import { InboxPage } from './pages/InboxPage';
import { JournalVerificationPage } from './pages/JournalVerificationPage';
import { InvoiceRunsPage } from './pages/InvoiceRunsPage';
// 2026-05-19: BankAutoDebitInvoicePage は JournalVerificationPage 内 sub-tab で render される。直 import 不要
import { FileboxProcessingPage } from './pages/FileboxProcessingPage';
import { FixedJournalVerificationPage } from './pages/FixedJournalVerificationPage';
import { BankProcessingPage } from './pages/BankProcessingPage';
import { AutoRegisterRuleListPage } from './pages/AutoRegisterRuleListPage';
import { PartnerHealthPage } from './pages/PartnerHealthPage';
import { PartnerUnregisteredPage } from './pages/PartnerUnregisteredPage';
import { SectionUnknownPage } from './pages/SectionUnknownPage';
import { DuplicateCandidatesPage } from './pages/DuplicateCandidatesPage';
import { PayrollPage } from './pages/PayrollPage';
import { AlsokPage } from './pages/AlsokPage';
import { LearningPage } from './pages/LearningPage';
import { FinancialStatementsPage } from './pages/FinancialStatementsPage';
import { NippouPage } from './pages/NippouPage';
import { CashbooksPage } from './pages/CashbooksPage';
import { PettyCashPage } from './pages/PettyCashPage';
import { MonthEndJournalPage } from './pages/MonthEndJournalPage';
import { MarketRentPage } from './pages/MarketRentPage';
import { BudgetActualPage } from './pages/BudgetActualPage';
import { DeptSummaryPage } from './pages/DeptSummaryPage';
import { JournalReviewPage } from './pages/JournalReviewPage';
import { BatchesPage } from './pages/BatchesPage';
import { FindingsPage } from './pages/FindingsPage';
import { RulebookPage } from './pages/RulebookPage';
import { BudgetCreatePage } from './pages/BudgetCreatePage';
import { BreakEvenPage } from './pages/BreakEvenPage';
import { PaymentAgingPage } from './pages/PaymentAgingPage';

// readPageFromHash — 全 hash 後方互換 (旧 deep link を全て維持)。
//   2026-07-05 UI刷新: fallback を 'invoice' → 'inbox' に変更 (受信箱=ホーム)。
//   legacy redirect: #deferred / #bank_auto_debit(_invoice) → 'invoice' (経緯は旧 App.tsx 参照)
function readPageFromHash(): PageKey {
  if (typeof window === 'undefined') return 'inbox';
  const h = window.location.hash.toLowerCase();
  if (/^#?inbox(\/|$)/.test(h)) return 'inbox';
  // 2026-05-11 Team V: legacy `#deferred` URL は backward compat で `invoice` page を返すのみ。
  if (/^#?deferred(\/|$)/.test(h)) return 'invoice';
  // 2026-07-07: 請求書処理run (カーソル起点一括処理)。#invoice より前に判定
  //   ('#invoice_runs' は下の /^#?invoice(\/|$)/ にはマッチしないが、明示的に先へ置く)
  if (/^#?invoice_runs(\/|$)/.test(h)) return 'invoice_runs';
  // 2026-05-19: bank_auto_debit_invoice 単独 page 廃止 → 'invoice' page に統合 (sub-tab)。
  //   注: '#invoice/' で始まる URL は全て 'invoice' page (sub-tab 解決は JournalVerificationPage 側)
  if (/^#?invoice(\/|$)/.test(h)) return 'invoice';
  if (/^#?bank_auto_debit(_invoice)?(\/|$)/.test(h)) return 'invoice';
  if (/^#?learning(\/|$)/.test(h)) return 'learning';
  if (/^#?diagnosis(\/|$)/.test(h)) return 'diagnosis';
  if (/^#?financial(\/|$)/.test(h)) return 'financial';
  if (/^#?budget_actual(\/|$)/.test(h)) return 'budget_actual';
  if (/^#?dept_summary(\/|$)/.test(h)) return 'dept_summary';
  if (/^#?budget_create(\/|$)/.test(h)) return 'budget_create';
  if (/^#?breakeven(\/|$)/.test(h)) return 'breakeven';
  if (/^#?partner_unregistered(\/|$)/.test(h)) return 'partner_unregistered';
  if (/^#?section_unknown(\/|$)/.test(h)) return 'section_unknown';
  if (/^#?duplicate_candidates(\/|$)/.test(h)) return 'duplicate_candidates';
  if (/^#?findings(\/|$)/.test(h)) return 'findings';
  if (/^#?rulebook(\/|$)/.test(h)) return 'rulebook';
  if (/^#?rules(\/|$)/.test(h)) return 'rules';
  if (/^#?payroll(\/|$)/.test(h)) return 'payroll';
  if (/^#?nippou(\/|$)/.test(h)) return 'nippou';
  if (/^#?alsok(\/|$)/.test(h)) return 'alsok';
  // 2026-07-22: 西武/小口現金/室長立替金 を「出納帳」1ページに統合。legacy hash は互換維持
  if (/^#?cashbooks(\/|$)/.test(h)) return 'cashbooks';
  if (/^#?seibu_ledger(\/|$)/.test(h)) return 'cashbooks';
  if (/^#?payment_aging(\/|$)/.test(h)) return 'payment_aging';
  if (/^#?petty_cash(\/|$)/.test(h)) return 'cashbooks';
  if (/^#?market_rent(\/|$)/.test(h)) return 'market_rent';
  if (/^#?journal_review(\/|$)/.test(h)) return 'journal_review';
  if (/^#?batches(\/|$)/.test(h)) return 'batches';
  if (/^#?month_end(\/|$)/.test(h)) return 'month_end';
  if (/^#?bank(\/|$)/.test(h)) return 'bank';
  if (/^#?fixed(\/|$)/.test(h)) return 'fixed';
  if (/^#?filebox(\/|$)/.test(h)) return 'filebox';
  // fallback: 受信箱ホーム (strict prefix match — h.includes は誤マッチを起こすため不使用)
  return 'inbox';
}

// 小口現金 提出キオスク (2026-07-22): 店舗 iPhone のホーム画面アイコン用。
//   #petty_cash/submit?s=<section_id> はシェル(左レール/ドック)なしで提出画面のみ表示。
//   店舗スタッフ端末に経理ハブ全体を見せないための分離でもある。
function isPettyCashKiosk(): boolean {
  if (typeof window === 'undefined') return false;
  return /^#?petty_cash\/submit(\?|$)/.test(window.location.hash.replace(/^#/, '#').toLowerCase());
}

function App() {
  const [page, setPage] = useState<PageKey>(readPageFromHash());
  const [kiosk, setKiosk] = useState<boolean>(isPettyCashKiosk());

  useEffect(() => {
    const onHashChange = () => { setPage(readPageFromHash()); setKiosk(isPettyCashKiosk()); };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigateTo = useCallback((key: PageKey) => {
    setPage(key);
    window.location.hash = `#${key}`;
  }, []);

  if (kiosk) {
    return (
      <VibesProvider lang="ja">
        <PettyCashPage />
      </VibesProvider>
    );
  }

  return (
    <VibesProvider lang="ja">
      <AppShell page={page} onNavigate={navigateTo}>
        {/* 25日払いカバレッジ帯 — コンテンツ領域内に常駐 (P0)。受信箱カード統合は P2 */}
        <div className="pa-no-print"><InvoiceCoverageBanner /></div>
        {page === 'inbox' && <InboxPage />}
        {page === 'invoice' && <JournalVerificationPage />}
        {page === 'invoice_runs' && <InvoiceRunsPage />}
        {page === 'nippou' && <NippouPage />}
        {page === 'duplicate_candidates' && <DuplicateCandidatesPage />}
        {page === 'fixed' && <FixedJournalVerificationPage />}
        {page === 'filebox' && <FileboxProcessingPage />}
        {page === 'bank' && <BankProcessingPage />}
        {page === 'cashbooks' && <CashbooksPage />}
        {page === 'payment_aging' && <PaymentAgingPage />}
        {page === 'month_end' && <MonthEndJournalPage />}
        {page === 'market_rent' && <MarketRentPage />}
        {page === 'journal_review' && <JournalReviewPage />}
        {page === 'batches' && <BatchesPage />}
        {page === 'findings' && <FindingsPage />}
        {page === 'alsok' && <AlsokPage />}
        {page === 'payroll' && <PayrollPage />}
        {page === 'diagnosis' && <PartnerHealthPage />}
        {page === 'partner_unregistered' && <PartnerUnregisteredPage />}
        {page === 'section_unknown' && <SectionUnknownPage />}
        {page === 'rules' && <AutoRegisterRuleListPage />}
        {page === 'rulebook' && <RulebookPage />}
        {page === 'learning' && <LearningPage />}
        {page === 'financial' && <FinancialStatementsPage />}
        {page === 'budget_actual' && <BudgetActualPage />}
        {page === 'dept_summary' && <DeptSummaryPage />}
        {page === 'budget_create' && <BudgetCreatePage />}
        {page === 'breakeven' && <BreakEvenPage />}
      </AppShell>
      {/* 全画面常駐 dock 類 (fixed 配置のためシェル外にマウント) */}
      <div className="pa-no-print">
        <CommandDock />
        <JobTray />
      </div>
    </VibesProvider>
  );
}

export default App;
